import type { AppContext, NutritionArea } from "@/types/scheduling";
import { getBusinessName } from "@/utils/businessLeads";
import { getStage } from "@/utils/functions";
import { getQuizInstance, getSSLocalStorage } from "@/utils/localStorage";
import { dietitianListStaging, dietitianListWInsurance, insuranceList } from "shared/constants";
import type { HealthieId, InsuranceMap, InsuranceValue, PaymentType } from "shared/types";
import { UsaStates } from "usa-states";

const isDev = process.env.NODE_ENV === "development";
const stage = getStage(isDev);

// TODO check all other state labels
export const usaStates = new UsaStates().states.map((state) => {
  if (state.abbreviation === "DC") state.name = "District of Columbia";
  return { value: state.abbreviation, label: state.name };
});

export const bannedStates = ["ID", "RI", "DE"];

export const nutritionAreas: NutritionArea[] = [
  { value: "Weight Loss", label: "Weight Loss", synonyms: ["Weight Management"] },
  { value: "General Health", label: "General Health", synonyms: ["Overall Health & Wellness", "Overall Health"] },
  { value: "Diabetes or Prediabetes", label: "Diabetes or Prediabetes", synonyms: ["Diabetes"] },
  { value: "Kidney Health", label: "Kidney Health", synonyms: ["Renal Health", "Kidney Disease"] },
  {
    value: "Sports Performance",
    label: "Sports Performance",
    synonyms: ["Athlete Nutrition", "Sports & Athletic Performance", "Sports", "Athlete"],
  },
  { value: "Weight Gain", label: "Weight Gain" },
  { value: "Liver Health Expertise", label: "Liver Health" },
  { value: "Hormonal Health", label: "Hormonal Health" },
  { value: "Pregnancy & Women's Health", label: "Pregnancy & Women's Health", synonyms: ["Women's Health", "Women"] },
  {
    value: "Longevity and Healthy Aging",
    label: "Longevity and Healthy Aging",
    synonyms: ["Aging Adults", "Longevity"],
  },
  { value: "Digestive (Gut Health)", label: "Digestive (Gut Health)", synonyms: ["Gut Health", "Digestive"] },
  { value: "Eating Disorders", label: "Eating Disorders", synonyms: ["Disordered Eating"] },
  { value: "Autoimmune Diseases", label: "Autoimmune Diseases", synonyms: ["Autoimmune Disease", "Autoimmune"] },
  { value: "Pediatrics", label: "Pediatrics", synonyms: ["Children's Health", "Pediatric"] },
  { value: "Cancer Expertise", label: "Cancer Expertise", synonyms: ["Cancer"] },
  { value: "Heart Health Expertise", label: "Heart Health Expertise", synonyms: ["Heart Health"] },
  { value: "Other", label: "Other" },
] as const;

export enum ParameterRDEMapEnum {
  DiabetesOrPrediabetes = "Diabetes or Prediabetes",
  WeightLoss = "Weight Loss",
  GeneralHealth = "General Health",
  InsulinOrPump = "Insulin or Pump",
}

const parameterRDEMapProd: Record<ParameterRDEMapEnum, string[]> = {
  [ParameterRDEMapEnum.DiabetesOrPrediabetes]: [
    "3197516", // Shelby Love
    "2034017", // Colette Micko
    "2029699", // Joanne Tippin
    "2874526", // Valentina Lemes
    "1168412", // Emma Liu
    // "1536013", // Lena Bakovic
    "3180836", // Rebecca Dillon
  ],
  [ParameterRDEMapEnum.WeightLoss]: ["1451394", "2186317", /*"1361835",*/ /*"1485584",*/ "1540166", "2874526", "1168412"],
  [ParameterRDEMapEnum.GeneralHealth]: ["1540166", "2874526"],
  [ParameterRDEMapEnum.InsulinOrPump]: ["3197516", "2034017", "2029699"],
};

const parameterRDEMapStaging: Record<ParameterRDEMapEnum, string[]> = {
  [ParameterRDEMapEnum.DiabetesOrPrediabetes]: ["590867"],
  [ParameterRDEMapEnum.WeightLoss]: ["117997"],
  [ParameterRDEMapEnum.GeneralHealth]: ["117996"],
  [ParameterRDEMapEnum.InsulinOrPump]: ["590867"],
};

export const parameterRDEMap = stage === "prod" ? parameterRDEMapProd : parameterRDEMapStaging;

//TODO modify communicationStyles to work as an enum
export const communicationStyles = [
  { value: "Zoom Video Call", label: "Zoom Video Call" },
  { value: "FaceTime Call", label: "FaceTime Call" },
  { value: "Android Video Call", label: "Android Video Call" },
];

export const paymentMethods: { value: PaymentType; label: string }[] = [
  { value: "insurance", label: "Insurance" },
  { value: "hsa_fsa", label: "HSA/FSA" },
  // { value: 'superbill', label: 'Superbill' },
  { value: "pay_out_of_pocket", label: "Pay out of pocket" },
];

export interface PricingOptionContent {
  id: number;
  name: string;
  best: boolean;
  shown: boolean;
  header: string | null;
  title: string;
  subtitle: string;
  description: string;
  currentPrice: string;
  frequency: string;
  originalPrice: string | null;
  discountBadge: string | null;
  features: string[];
  cta: string;
  apptDuration?: string;
  apptFrequency?: string;
}

export const pricingOptions: PricingOptionContent[] = [
  {
    id: 1,
    name: "weekly",
    best: false,
    shown: true,
    header: "Most Popular",
    title: "Weekly",
    subtitle: "4 Video Calls Per Month",
    description: "Only $60 per session",
    currentPrice: "$240",
    frequency: "/monthly",
    originalPrice: "$344",
    discountBadge: "Save 30%",
    features: [
      "Initial consultation",
      "Weekly video call sessions",
      "Unlimited texting support",
      "Access to lab tests and diagnostics",
      "Exclusive discounts on supplements",
      "Change plans anytime",
    ],
    cta: "Get Started",
    apptDuration: "60 min",
    apptFrequency: "4 sessions/month",
  },
  {
    id: 2,
    name: "text-monthly",
    best: true,
    shown: true,
    header: "Best Weight Loss Results",
    title: "ThriveWell",
    subtitle: "1 video call and unlimited texting",
    description: "Only $3 per day",
    currentPrice: "$99",
    frequency: "/monthly",
    originalPrice: "$142",
    discountBadge: "Save 30%",
    features: [
      "1 hour initial consultation",
      "Unlimited texting support",
      "Access to lab tests and diagnostic",
      "Exclusive discounts on supplements",
      "Change plans anytime",
    ],
    cta: "Get Started",
    apptDuration: "-",
    apptFrequency: "Unlimited",
  },
  {
    id: 3,
    name: "bi-weekly",
    best: false,
    shown: false,
    header: null,
    title: "Bi-Weekly",
    subtitle: "2 video calls per month",
    description: "Only $90 per session",
    currentPrice: "$180",
    frequency: "/monthly",
    originalPrice: "$258",
    discountBadge: "Save 30%",
    features: [
      "Initial consultation",
      "Bi-Weekly video call sessions",
      "Unlimited texting support",
      "Access to lab tests and diagnostics",
      "Exclusive discounts on supplements",
      "Change plans anytime",
    ],
    cta: "Learn More",
    apptDuration: "60 min",
    apptFrequency: "2 sessions/month",
  },
  {
    id: 4,
    name: "text-monthly",
    best: false,
    shown: true,
    header: "Best Weight Loss Results",
    title: "Single Session",
    subtitle: "Video call",
    description: "1 hour video call with a Registered Dietitian",
    currentPrice: "$99",
    frequency: "single purchase",
    originalPrice: null,
    discountBadge: null,
    features: [
      "1 initial consultation",
      "Unlimited texting support",
      "Access to lab testing and diagnostics",
      "Exclusive discounts on supplements",
      "No long term commitments",
    ],
    cta: "Learn More",
    apptDuration: "-",
    apptFrequency: "Unlimited",
  },
  {
    id: 6,
    name: "insurance-session-60m",
    best: true,
    shown: true,
    header: null,
    title: "Dietitian consult",
    subtitle: "60 min video call",
    description: "",
    currentPrice: "$0",
    frequency: "out of pocket",
    originalPrice: null,
    discountBadge: null,
    features: [
      "Initial consultation",
      "Unlimited texting support",
      "Follow-up sessions: Weekly, biweekly, or monthly available",
      "Access to lab testing and diagnostics",
      "Exclusive discounts on supplements",
    ],
    cta: "Continue",
    apptDuration: "60 min",
    apptFrequency: "4/month",
  },
  {
    id: 7,
    name: "insurance-session-30m",
    best: true,
    shown: true,
    header: null,
    title: "Dietitian consult",
    subtitle: "30 min video call",
    description: "",
    currentPrice: "$0",
    frequency: "out of pocket",
    originalPrice: null,
    discountBadge: null,
    features: [
      "Video consultation",
      "Unlimited texting support",
      "Follow-up sessions: Weekly, biweekly, or monthly available",
      "Access to lab testing and diagnostics",
      "Exclusive discounts on supplements",
    ],
    cta: "Continue",
    apptDuration: "30 min",
    apptFrequency: "4/month",
  },
  {
    id: 8,
    name: "insurance-twiceweekly-60m",
    best: true,
    shown: true,
    header: "Best Results",
    title: "Premium plan",
    subtitle: "8 session/month (60 min video call)",
    description: "",
    currentPrice: "$0",
    frequency: "out of pocket",
    originalPrice: null,
    discountBadge: null,
    features: [
      "Video consultation",
      "Unlimited texting support",
      "Access to lab testing and diagnostics",
      "Exclusive discounts on supplements",
    ],
    cta: "Continue",
    apptDuration: "60 min",
    apptFrequency: "8 session/month",
  },
  {
    id: 9,
    name: "insurance-weekly-60m",
    best: false,
    shown: true,
    header: null,
    title: "Standard plan",
    subtitle: "4 session/month (60 min video call)",
    description: "",
    currentPrice: "$0",
    frequency: "out of pocket",
    originalPrice: null,
    discountBadge: null,
    features: [
      "Video consultation",
      "Unlimited texting support",
      "Access to lab testing and diagnostics",
      "Exclusive discounts on supplements",
    ],
    cta: "Continue",
    apptDuration: "60 min",
    apptFrequency: "4 session/month",
  },
  {
    id: 10,
    name: "insurance-biweekly-60m",
    best: false,
    shown: true,
    header: null,
    title: "Basic plan",
    subtitle: "2 session/month (60 min video call)",
    description: "",
    currentPrice: "$0",
    frequency: "out of pocket",
    originalPrice: null,
    discountBadge: null,
    features: [
      "Video consultation",
      "Unlimited texting support",
      "Access to lab testing and diagnostics",
      "Exclusive discounts on supplements",
    ],
    cta: "Continue",
    apptDuration: "60 min",
    apptFrequency: "2/month",
  },
];

export const initialContext: AppContext = {
  result: {
    nutrition_area: {
      value: "",
      label: "",
      key: "",
    },
    state: {
      value: "",
      label: "",
      key: "",
    },
    day: {
      value: "",
      label: "",
      key: "",
    },
    communication_style: {
      value: "",
      label: "",
      key: "",
    },
    timezone: "",
    payment_method: {
      value: "",
      label: "",
      key: "",
    },
    insurance_provider_other: "",
    lead_name: "",
    lead_email: "",
    lead_phone_number: "",
    insurance_provider: {
      value: "",
      label: "",
      key: "",
    },
    provider_id: "",
    date_time: "",
    business_name: getBusinessName() || null,
  },
  error_message: undefined,
  app_loading_element: (document.querySelector(".loading-container") as HTMLElement) || null,
  flow: {
    name: "quiz_flow",
    id: "Quiz - Insurance",
  },
  provider_id: undefined,
  provider_ids: [],
  plan_id: undefined,
  slots: undefined,
  state_options: usaStates,
  nutrition_area_options: nutritionAreas,
  payment_method_options: paymentMethods,
  insurance_provider_options: [...insuranceList.filter((insurance) => insurance.visible)],
  available_expertise_areas: [],
  available_states: [],
  step: "",
  next_allowed: {
    insurance_selection_other: false,
    lead_name: false,
    lead_email: false,
    lead_phone_number: false,
  },
  wanted_expertise: [],
  day_slots: [],
  rde_slots: [],
  provider_data: undefined,
  provider_data_raw: undefined,
  pulled_provider_ids: [],
  applied_slot_filters: false,
  internal_completion: {
    used_only_url_params: false,
    used_only_quiz_params: false,
  },
  hide_back_button: {
    day_selector: false,
  },
  prior_data: {
    quiz_instance: getQuizInstance(),
    self_serve: getSSLocalStorage(),
  },
};

// Create a function to initialize and populate the map
function createAndPopulateInsuranceMap(): InsuranceMap {
  const map: InsuranceMap = new Map(
    insuranceList.map((insurance) => [
      insurance.value as InsuranceValue,
      { name: insurance.label, dietitians: new Set<HealthieId>() },
    ]),
  );
  // Populate the map with dietitians
  if (stage === "staging" || stage === "local") {
    dietitianListStaging.forEach((dietitian) => {
      dietitian.insurances.forEach((insuranceKey) => {
        const insuranceInfo = map.get(insuranceKey);
        if (insuranceInfo) {
          insuranceInfo.dietitians.add(dietitian.healthieId);
        }
      });
    });
  } else {
    dietitianListWInsurance.forEach((dietitian) => {
      dietitian.insurances.forEach((insuranceKey) => {
        const insuranceInfo = map.get(insuranceKey as InsuranceValue);
        if (insuranceInfo) {
          insuranceInfo.dietitians.add(dietitian.healthieId);
        }
      });
    });
  }

  return map;
}

export const insuranceMap = createAndPopulateInsuranceMap();

export const dateFormatString = "yyyy-MM-dd HH:mm:ss xxxx";
